import React from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { AnyAction } from 'redux';
import { withTranslation } from 'react-i18next';
import { RootState } from '../redux/store/reducers';
import { ISummary } from '../redux/summaries/types';
import { getSummaryThunk, getRecordSummariesThunk, createSummaryThunk, deleteSummaryThunk } from '../redux/summaries/thunks';
import { actions as summaryActions } from '../redux/summaries/slice';
import { Plus, Share2, Trash2, FileText } from 'lucide-react';
import { openCenteredPopup80, timeShortDisplay } from 'utils/utils';
import { toast } from 'react-toastify';
import Spinner from './Spinner';
import { IRecord } from 'redux/records/types';
import { handlePdfDownload } from 'services/rest';
import { UserType } from 'redux/auth/types';

/**
 * Component for managing and displaying summaries associated with a specific record.
 */
class RecordSummariesList extends React.Component<SummaryListProps> {
  /**
   * Fetches record summaries when the component mounts
   */
  componentDidMount(): void {
    const { currentRecord } = this.props;
    if (!currentRecord.onroute) this.props.getRecordSummariesThunk(currentRecord.record_id);
  }

  /**
   * Fetches record summaries when record ID changes
   * @param {SummaryListProps} prevProps - Previous component props
   */
  componentDidUpdate(prevProps: SummaryListProps) {
    const { currentRecord } = this.props;
    if (!currentRecord.onroute && currentRecord.record_id !== prevProps.currentRecord.record_id) this.props.getRecordSummariesThunk(currentRecord.record_id);
  }

  /**
   * Renders the component
   * @returns {JSX.Element} The rendered component
   */
  render() {
    const { summaries = [], currentRecord, userId, userType, loading, error, t } = this.props;

    return (
      <div className='page body-container'>
        <div className='header m-n-relation'>
          <span>{t('summaries.title')}</span>
          {userType === UserType.Admin && (
            <button
              onClick={async () => {
                const direction = this.props.i18n.language === 'he' ? 'rtl' : 'ltr';
                await this.props.createSummaryThunk({ recordId: currentRecord.record_id, direction });
                this.props.setSelectedSummary(null);
              }}
              className='action-button add'
            >
              <Plus />
            </button>
          )}
        </div>

        <div className='summaries-container'>
          <div className='data-container'>
            {summaries.length > 0 ? (
              summaries.map((summary) => (
                <div key={summary.id} className={`table-row summary`}>
                  <div className='created-at' data-title={t('common.fields.createdAt')} title={t('common.fields.createdAt')}>
                    {timeShortDisplay(new Date(summary.created_at))}
                  </div>
                  <div className='by' data-title={t('common.fields.subject')}>
                    {summary.subject}
                  </div>
                  <div className='by' data-title={t('common.by')}>
                    {summary.saas_tenant_name}
                  </div>
                  <div className='actions' data-title={t('common.fields.actions')}>
                    <button className='action-button pdf' title={t('summaries.downloadPdf')} onClick={() => this.handlePdfDownload(summary)}>
                      <FileText />
                    </button>
                    <button
                      className='action-button share'
                      title={t('summaries.shareWhatsapp')}
                      onClick={async () => {
                        const pdf_url: string | null = await handlePdfDownload(summary.id, this.props.JWT as string);
                        if (pdf_url) this.handleShareViaWhatsApp(pdf_url);
                        else toast.error(t('messages.error'));
                      }}
                    >
                      <Share2 />
                    </button>
                    {userId === summary.saas_tenant_id && (
                      <button onClick={() => this.handleDeleteSummary(summary)} className='action-button delete' title={t('common.delete')}>
                        <Trash2 />
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : loading ? (
              <Spinner />
            ) : (
              error && <span className='error-message'>{error}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  /**
   * Handles the PDF download process for a summary, including waiting for recent updates
   * to be processed by the backend stream processor if necessary.
   * @param {ISummary} summary - The summary to download as PDF
   * @returns {Promise<void>}
   * @private
   */
  private async handlePdfDownload(summary: ISummary): Promise<void> {
    const { t } = this.props;

    // Wait at least 10 seconds after summary update before allowing PDF download
    const timeDelta = new Date().getTime() - new Date(summary.created_at).getTime();
    if (timeDelta < 10000) {
      const sleepTime = 10000 - timeDelta;
      await new Promise((resolve) => setTimeout(resolve, sleepTime));
    }

    // Download the PDF
    const pdfUrl: string | null = await handlePdfDownload(summary.id, this.props.JWT as string);
    if (!pdfUrl) toast.error(t('messages.error'));
    else if (timeDelta >= 3000) {
      openCenteredPopup80(pdfUrl);
    } else
      toast.success(
        <div>
          <a href={pdfUrl} target='_blank' rel='noopener noreferrer' style={{ color: 'blue', textDecoration: 'underline' }}>
            {summary.created_at}
          </a>
        </div>,
        { autoClose: 5000 },
      );
  }

  /**
   * Shares summary link via WhatsApp
   * @param {string} summaryId - ID of summary to share
   * @param {string} pdf_url - URL in CloudFront of the summary to share
   */
  handleShareViaWhatsApp = (pdf_url: string) => {
    const { t, currentRecord } = this.props;
    const message = t('recordSummaries.shareMessage', {
      subject: currentRecord.subject,
      origin: window.location.origin,
      url: pdf_url,
    });
    openCenteredPopup80(`https://wa.me/?text=${encodeURIComponent(message)}`);
  };

  /**
   * Handles summary deletion after user confirmation
   */
  handleDeleteSummary = async (summary: ISummary) => {
    const { t, currentRecord } = this.props;
    if (window.confirm(t('recordSummaries.confirmDelete', { name: summary.created_at }))) {
      try {
        await this.props.deleteSummaryThunk(summary.id);
        toast.success(t('messages.summaryDeleted'));
        // Refresh the summaries list
        await this.props.getRecordSummariesThunk(currentRecord.record_id);
      } catch (error) {
        toast.error(t('messages.error'));
      }
    }
  };
}

/**
 * Props interface for RecordSummariesList component
 * @interface SummaryListProps
 */
interface SummaryListProps {
  t: (key: string, options?: any) => string;
  i18n: any;
  currentRecord: IRecord;
  selectedSummary: ISummary | null;
  summaries: ISummary[];
  loading: boolean;
  error: string | null;
  JWT: string | null;
  userId: string;
  userType: string;
  username: string;
  getSummaryThunk: (summaryId: string) => void;
  getRecordSummariesThunk: (recordId: string) => void;
  createSummaryThunk: (payload: any) => void;
  deleteSummaryThunk: (summaryId: string) => Promise<any>;
  setSelectedSummary: (summary: ISummary | null) => void;
}

/**
 * Maps state to props for RecordSummariesList component
 */
const mapStateToProps = (state: RootState) => ({
  currentRecord: state.records.records.find((record) => record.record_id === state.records.currentRecordId) as IRecord,
  selectedSummary: state.summaries.selectedSummary,
  summaries: state.summaries.summaries,
  loading: state.summaries.loading,
  error: state.summaries.error,
  JWT: state.auth.JWT,
  userId: state.auth.userId as string,
  userType: state.auth.userType as string,
  username: state.auth.userName as string,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => ({
  getSummaryThunk: (summaryId: string) => dispatch(getSummaryThunk(summaryId)),
  getRecordSummariesThunk: (recordId: string) => dispatch(getRecordSummariesThunk(recordId)),
  createSummaryThunk: (payload: any) => dispatch(createSummaryThunk(payload)),
  deleteSummaryThunk: (summaryId: string) => dispatch(deleteSummaryThunk(summaryId)),
  setSelectedSummary: (summary: ISummary | null) => dispatch(summaryActions.setSelectedSummary(summary)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(RecordSummariesList));
